import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import {
	createStore,
	createHook,
	createActionsHook,
	type Action,
} from '@atlassian/react-sweet-state';
import { DRAG_AND_DROP_BUTTON_FOCUS_EVENT } from '../../common/constants.tsx';
import type { TableRef } from '../../common/types/base-table.tsx';
import { getColumnVisibilityIndexes } from '../../ui/list-view/base-table/utils.tsx';
import { TIMEOUT_DELAY } from './constants.tsx';
import type {
	State,
	Direction,
	ColumnIndex,
	RowIndex,
	CellCoordinates,
	CellCoordinatesById,
} from './types.tsx';
import {
	checkIsCellSelected,
	checkIsCellCopiedFrom,
	getCellSelectedSelector,
	getUpdatedNavigationIndexes,
	scrollCellIntoView,
	getHasSelectionSelector,
} from './utils.tsx';

const actions = {
	clearSelectedCell:
		(): Action<State> =>
		({ setState }) => {
			setState({ selectedCell: undefined });
		},
	clearCopiedFromCell:
		(): Action<State> =>
		({ setState }) => {
			setState({ copiedFromCell: null });
		},
	setSelectedCell:
		(columnIndex: ColumnIndex, rowIndex: RowIndex): Action<State> =>
		({ setState, getState }) => {
			const { selectedCell } = getState();
			if (checkIsCellSelected(selectedCell, columnIndex, rowIndex)) {
				return;
			}

			setState({ selectedCell: { column: columnIndex, row: rowIndex } });
		},
	setCopiedFromCell:
		(fieldId?: string, issueId?: string): Action<State> =>
		({ setState, getState }) => {
			if (!fieldId || !issueId) {
				return;
			}
			const { copiedFromCell } = getState();
			if (checkIsCellCopiedFrom(copiedFromCell, fieldId, issueId)) {
				return;
			}

			setState({ copiedFromCell: { fieldId, issueId } });
		},
	navigate:
		(direction: Direction, tableRef: TableRef, dragAndDropButtonRow?: number): Action<State> =>
		({ setState, getState }) => {
			const { selectedCell } = getState();
			const tableRefProps = tableRef?.current?.table?.props;
			const tableData = tableRefProps?.data;
			const tableWidth = tableRefProps?.width || 0;
			const scrollLeft = tableRef?.current?.table?.bodyRef?.state?.scrollLeft || 0;
			const tableColumns = tableRefProps?.columns || [];
			const { visibleColumns } = getColumnVisibilityIndexes(scrollLeft, tableWidth, tableColumns);

			// Handle navigation from drag and drop button to the last cell of the row above.
			if (dragAndDropButtonRow && tableData) {
				// stay on the same cell if the new row is a group item row
				if (isGroupItem(tableData?.[dragAndDropButtonRow - 1]?.issueId?.toString() || null)) {
					return;
				}

				scrollCellIntoView(
					tableRef,
					visibleColumns,
					direction,
					{ column: 0, row: dragAndDropButtonRow },
					{
						column: tableColumns.length - 1,
						row: dragAndDropButtonRow - 1,
					},
				);

				setTimeout(() => {
					setState({
						selectedCell: {
							column: tableColumns.length - 1,
							row: dragAndDropButtonRow - 1,
						},
					});
				}, TIMEOUT_DELAY);

				return;
			}

			if (!selectedCell || !tableData) {
				return;
			}

			const { newColumnIndex, newRowIndex } = getUpdatedNavigationIndexes(
				selectedCell,
				direction,
				tableColumns,
				tableData,
			);

			// stay on the same cell if the new row is a group item row
			if (tableData && isGroupItem(tableData?.[newRowIndex]?.issueId?.toString() || null)) {
				return;
			}

			scrollCellIntoView(tableRef, visibleColumns, direction, selectedCell, {
				column: newColumnIndex,
				row: newRowIndex,
			});

			const prevCellColumn = selectedCell.column;

			// Focus on the drag and drop button only if the last column was selected
			// and the next selected column and row is the first of the next row.
			const shouldFocusOnDragAndDropButton =
				prevCellColumn === tableColumns.length - 1 &&
				newColumnIndex === 0 &&
				newRowIndex < tableData.length;

			// wait for all scroll, focus and blur events to finish before setting state
			if (shouldFocusOnDragAndDropButton) {
				const { issueId } = tableData[newRowIndex];
				setTimeout(() => {
					if (typeof document !== 'undefined') {
						document.dispatchEvent(
							new CustomEvent(`${DRAG_AND_DROP_BUTTON_FOCUS_EVENT}`, {
								detail: { issueId },
							}),
						);
					}
				}, TIMEOUT_DELAY);
			} else {
				setTimeout(() => {
					setState({
						selectedCell: {
							column: newColumnIndex,
							row: newRowIndex,
						},
					});
				}, TIMEOUT_DELAY);
			}
		},
};

type Actions = typeof actions;

export const Store = createStore<State, Actions>({
	initialState: {
		selectedCell: undefined,
		copiedFromCell: null,
		prevScrolledColumnIndex: undefined,
	},
	actions,
	name: 'JwmCellNavigation',
});

export const useCellSelected = createHook<
	State,
	Actions,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	any,
	CellCoordinates & CellCoordinatesById
>(Store, {
	// we need to use the selector to prevent unnecessary re-render in all the cells every time the focus change
	selector: getCellSelectedSelector,
});

export const useHasCellSelection = createHook<State, Actions, { hasSelection: boolean }>(Store, {
	selector: getHasSelectionSelector,
});

export const useCellActions = createActionsHook<State, Actions>(Store);
